import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * MySQL Node Schema
 * Supports execute query, insert, update, delete, and table management operations
 */
const schema: NodePropertySchema = {
  nodeType: 'mysql',
  version: '2.5.0',

  properties: [
    // ==================================================================
    // CREDENTIAL SELECTOR (Must be first)
    // ==================================================================
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      description: 'Select the MySQL credential to use for authentication',
      credentialTypes: [{ name: 'mysql', required: true }],
    },

    // ==================================================================
    // OPERATION
    // ==================================================================
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'executeQuery',
      options: [
        {
          name: 'Execute Query',
          value: 'executeQuery',
          description: 'Execute a SQL query'
        },
        {
          name: 'Insert Rows',
          value: 'insert',
          description: 'Insert rows from input array into a table'
        },
        {
          name: 'Update Rows',
          value: 'update',
          description: 'Update rows in a table using input array'
        },
        {
          name: 'Delete Rows',
          value: 'delete',
          description: 'Delete rows matching a condition'
        },
        {
          name: 'Create Table',
          value: 'createTable',
          description: 'Create a table from array data structure'
        },
        {
          name: 'Bulk Insert',
          value: 'bulkInsert',
          description: 'Insert all rows from an array'
        },
        {
          name: 'Drop Table',
          value: 'dropTable',
          description: 'Drop (delete) a table'
        },
        {
          name: 'Truncate Table',
          value: 'truncateTable',
          description: 'Remove all rows from a table'
        },
        {
          name: 'Table Exists',
          value: 'tableExists',
          description: 'Check if a table exists'
        },
        {
          name: 'Get Table Schema',
          value: 'getTableSchema',
          description: 'Get column definitions for a table'
        }
      ],
      description: 'The operation to perform'
    },

    // ==================================================================
    // EXECUTE QUERY
    // ==================================================================
    {
      key: 'query',
      label: 'Query',
      type: 'string',
      typeOptions: {
        rows: 10,
        editor: 'code',
        editorLanguage: 'sql',
      },
      displayOptions: {
        show: {
          operation: ['executeQuery']
        }
      },
      placeholder: 'SELECT * FROM users WHERE status = @status',
      required: true,
      description: 'The SQL query to execute. Use @paramName for parameters.',
    },

    // ==================================================================
    // QUERY PARAMETERS (executeQuery only)
    // ==================================================================
    {
      key: 'parameters',
      label: 'Query Parameters',
      type: 'collection',
      displayOptions: {
        show: {
          operation: ['executeQuery']
        }
      },
      default: {},
      placeholder: 'Add Parameter',
      description: 'Query parameters for parameterized queries (use @paramName in query)',
      values: [
        {
          key: 'name',
          label: 'Parameter Name',
          type: 'string',
          default: '',
          placeholder: 'status',
        },
        {
          key: 'value',
          label: 'Value',
          type: 'string',
          default: '',
          placeholder: 'active',
        }
      ]
    },

    // ==================================================================
    // TABLE NAME (for table operations)
    // ==================================================================
    {
      key: 'table',
      label: 'Table',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['insert', 'update', 'delete', 'createTable', 'bulkInsert', 'dropTable', 'truncateTable', 'tableExists', 'getTableSchema']
        }
      },
      default: '',
      placeholder: 'users',
      required: true,
      description: 'Name of the table to operate on',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getTables',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ==================================================================
    // SOURCE ARRAY (for insert, update, createTable, bulkInsert)
    // ==================================================================
    {
      key: 'sourceArray',
      label: 'Source Data',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['insert', 'update', 'createTable', 'bulkInsert']
        }
      },
      placeholder: '{{ $node["NodeName"].array }}',
      required: true,
      description: 'Drag an array from a previous node. Each object in the array will be inserted/updated as a row.',
    },

    // ==================================================================
    // UPDATE - MATCH COLUMN
    // ==================================================================
    {
      key: 'matchColumn',
      label: 'Match Column',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['update']
        }
      },
      default: 'id',
      placeholder: 'id',
      required: true,
      description: 'Column to match rows for updating (typically the primary key). The source data must include this column.',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getColumns',
          dependsOn: ['table'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ==================================================================
    // DELETE - CONDITION FIELDS
    // ==================================================================
    {
      key: 'deleteColumn',
      label: 'Column',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['delete']
        }
      },
      default: '',
      placeholder: 'id',
      required: true,
      description: 'Column to use for matching rows to delete',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getColumns',
          dependsOn: ['table'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'deleteCondition',
      label: 'Condition',
      type: 'options',
      displayOptions: {
        show: {
          operation: ['delete']
        }
      },
      default: 'equals',
      required: true,
      options: [
        { name: 'Equals', value: 'equals', description: 'Column value equals the specified value' },
        { name: 'Not Equals', value: 'notEquals', description: 'Column value does not equal the specified value' },
        { name: 'Greater Than', value: 'greaterThan', description: 'Column value is greater than the specified value' },
        { name: 'Less Than', value: 'lessThan', description: 'Column value is less than the specified value' },
        { name: 'Greater Or Equal', value: 'greaterOrEqual', description: 'Column value is greater than or equal to the specified value' },
        { name: 'Less Or Equal', value: 'lessOrEqual', description: 'Column value is less than or equal to the specified value' },
        { name: 'Like', value: 'like', description: 'Column value matches the pattern (use % for wildcards)' },
        { name: 'In List', value: 'in', description: 'Column value is in the comma-separated list of values' },
        { name: 'Is Null', value: 'isNull', description: 'Column value is NULL' },
        { name: 'Is Not Null', value: 'isNotNull', description: 'Column value is not NULL' },
      ],
      description: 'How to match rows for deletion',
    },
    {
      key: 'deleteValue',
      label: 'Value',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['delete'],
          deleteCondition: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterOrEqual', 'lessOrEqual', 'like', 'in']
        }
      },
      default: '',
      placeholder: '123',
      required: true,
      description: 'Value to compare against. For "In List", use comma-separated values.',
    },

    // ==================================================================
    // DROP TABLE OPTIONS
    // ==================================================================
    {
      key: 'ifExists',
      label: 'Only If Exists',
      type: 'boolean',
      displayOptions: {
        show: {
          operation: ['dropTable']
        }
      },
      default: true,
      description: 'Use IF EXISTS to prevent errors if the table does not exist',
    },

    // ==================================================================
    // CREATE TABLE OPTIONS
    // ==================================================================
    {
      key: 'dropIfExists',
      label: 'Drop If Exists',
      type: 'boolean',
      displayOptions: {
        show: {
          operation: ['createTable']
        }
      },
      default: false,
      description: 'Drop the table first if it already exists',
    },
    {
      key: 'insertData',
      label: 'Insert Data After Create',
      type: 'boolean',
      displayOptions: {
        show: {
          operation: ['createTable']
        }
      },
      default: true,
      description: 'Insert the array data into the table after creating it',
    },

    // ==================================================================
    // SETTINGS
    // ==================================================================
    {
      key: 'queryTimeout',
      label: 'Query Timeout (seconds)',
      type: 'number',
      section: 'settings',
      default: 120,
      placeholder: '120',
      description: 'Maximum time to wait for query execution.',
      typeOptions: {
        minValue: 1
      },
    },
  ]
};

export default schema;
